import React, { useEffect, useMemo } from 'react';
import { ThreeElements } from '@react-three/fiber';
import { ColorRepresentation, ExtrudeGeometry, Shape } from 'three';

import { GRID_WIDTH } from '../../../../util/designGrid';
import {
  PLANE_VISUAL_BASE_LINE_WIDTH,
  PLANE_VISUAL_DEPTH,
  PLANE_VISUAL_TEETH_PER_GRID_LENGTH,
  PLANE_VISUAL_TOOTH_SIZE,
} from './constants';

const EXTRUSION_OPTIONS = { steps: 1, depth: PLANE_VISUAL_DEPTH, bevelEnabled: false };

function calculateToothPositions(segmentLength: number, toothLength: number) {
  const teethCoverageLength = segmentLength - toothLength;
  if (teethCoverageLength < toothLength) return null;

  const relativeSegmentSize = teethCoverageLength / GRID_WIDTH;

  const teethNeeded = Math.max(1, Math.floor(PLANE_VISUAL_TEETH_PER_GRID_LENGTH * relativeSegmentSize));
  const distanceBetweenTeeth = teethCoverageLength / (teethNeeded + 1);

  return Array.from({ length: teethNeeded }, (_, i) => distanceBetweenTeeth * (i + 1));
}

function drawPlaneDefinitionShape(length: number): Shape | null {
  const toothPositions = calculateToothPositions(length, PLANE_VISUAL_TOOTH_SIZE);
  if (toothPositions == null) return null;

  // triangular teeth on one side to signify directionality

  const shape = new Shape().moveTo(0, 0);
  for (const position of toothPositions) {
    shape
      .lineTo(position, 0)
      .lineTo(position + PLANE_VISUAL_TOOTH_SIZE / 2, PLANE_VISUAL_TOOTH_SIZE)
      .lineTo(position + PLANE_VISUAL_TOOTH_SIZE, 0);
  }

  shape.lineTo(length, 0);
  shape.lineTo(length, -PLANE_VISUAL_BASE_LINE_WIDTH).lineTo(0, -PLANE_VISUAL_BASE_LINE_WIDTH).lineTo(0, 0);

  return shape;
}

function drawVerticalPlaneDefinitionShape(length: number): Shape | null {
  const toothPositions = calculateToothPositions(length, PLANE_VISUAL_TOOTH_SIZE);
  if (toothPositions == null) return null;

  const shape = new Shape().moveTo(0, 0);

  // square/diamond teeth on both sides to signify verticality

  for (const position of toothPositions) {
    shape
      .lineTo(position, 0)
      .lineTo(position + PLANE_VISUAL_TOOTH_SIZE / 2, PLANE_VISUAL_TOOTH_SIZE / 2)
      .lineTo(position + PLANE_VISUAL_TOOTH_SIZE, 0);
  }

  toothPositions.reverse();
  shape.lineTo(length, 0).lineTo(length, -PLANE_VISUAL_BASE_LINE_WIDTH);

  for (const position of toothPositions) {
    shape
      .lineTo(position + PLANE_VISUAL_TOOTH_SIZE, -PLANE_VISUAL_BASE_LINE_WIDTH)
      .lineTo(position + PLANE_VISUAL_TOOTH_SIZE / 2, -(PLANE_VISUAL_TOOTH_SIZE / 2 + PLANE_VISUAL_BASE_LINE_WIDTH))
      .lineTo(position, -PLANE_VISUAL_BASE_LINE_WIDTH);
  }

  shape.lineTo(0, -PLANE_VISUAL_BASE_LINE_WIDTH).lineTo(0, 0);

  return shape;
}

export function PlaneDefinitionMesh(
  props: { length: number; vertical: boolean; color: ColorRepresentation } & ThreeElements['mesh'],
) {
  const { length, vertical, color, ...meshProps } = props;

  const geometry = useMemo(() => {
    const shape = vertical ? drawVerticalPlaneDefinitionShape(length) : drawPlaneDefinitionShape(length);

    if (shape == null) return null;

    return new ExtrudeGeometry(shape, EXTRUSION_OPTIONS).translate(
      -(length / 2), // the shape was drawn starting from the origin, but we want it to be centered horizontally
      vertical
        ? PLANE_VISUAL_BASE_LINE_WIDTH / 2 // keep visual centered on definition line since it is vertical
        : PLANE_VISUAL_BASE_LINE_WIDTH, // teeth were drawn at base y=0 for simplicity with the base line extending below
      0,
    );
  }, [length, vertical]);

  useEffect(() => () => geometry?.dispose(), [geometry]);

  if (geometry == null) return null;

  return (
    <mesh {...meshProps}>
      <meshBasicMaterial color={props.color} />
      <primitive attach="geometry" object={geometry} />
    </mesh>
  );
}
